import pageBuilderStore from "./pageBuilderStore";
import { ILayoutStyles, ITypographyRole, ITypoStyles, IBodyStyles } from "./state-interfaces";
import type { TextTransformType } from "./state-interfaces";
import blocksManager from "./blocks-manager";

/**
 * List of core typography roles used throughout the PageBuilder for consistent
 * serialization, UI rendering, and mapping between selectors and internal state.
 *
 * @constant {Array<keyof ITypoStyles>}
 * @description
 * Includes all default HTML/text roles supported by the system (headings, paragraph, caption, etc.).
 * Used to loop through, validate, and serialize core typography settings.
 * Custom roles/selectors are not included here; use Object.keys(typography) to get all.
 *
 * @example
 *   // Looping over all default roles
 *   TYPO_KEYS.forEach(role => { ... });
 */
export const TYPO_KEYS: (keyof ITypoStyles)[] = [
  "h1", "h2", "h3", "h4", "h5", "h6", "p", "caption", "quote", "links"
];

class BodyStylesManager {

  /**
   * Sets the currently active text role (selector) for typography editing in the sidebar.
   * Used to specify which default selector (e.g., "p", "quote", "link", "h1", "h2", "h3", etc.)
   * the user is currently editing or previewing in the Typography section.
   *
   * @param {string} role - The text role or selector name to set as active.
   *
   * @example
   * // Switch to editing the H2 typography style
   * bodyStylesManager.setActiveTextRole('h2');
   */
  setActiveTextRole(role: string) {
    pageBuilderStore.bodyStylesState.activeTextRole = role;
  }

  /**
   * Retrieves the currently active text role (selector) for typography editing.
   * Indicates which selector (e.g., "p", "quote", "link", "h1", "h2", "h3", etc.)
   * the user is currently modifying in the Typography section.
   *
   * @returns {string} The active text role or selector name.
   *
   * @example
   * const role = bodyStylesManager.getActiveTextRole();
   * // role might be 'h2', 'p', or 'quote'
   */  
  getActiveTextRole(): string {
    return pageBuilderStore.bodyStylesState.activeTextRole;
  }

  /**
   * Sets the currently active heading level for typography editing.
   * Used when the user selects a specific heading level (e.g., "h1", "h2", etc.) in the Typography section
   * to preview or adjust its styles.
   *
   * @param {string} level - The heading level to set as active (e.g., "h1", "h2", "h3").
   *
   * @example
   * // Set the active heading to H3 for style adjustments
   * bodyStylesManager.setActiveHeadingLevel('h3');
   */  
  setActiveHeadingLevel(level: string) {
    pageBuilderStore.bodyStylesState.activeHeadingLevel = level;
  }

  /**
   * Retrieves the currently active heading level for typography editing.
   * Indicates which heading selector (e.g., "h1", "h2", "h3") is currently being edited or previewed.
   *
   * @returns {string} The active heading level.
   *
   * @example
   * const level = bodyStylesManager.getActiveHeadingLevel();
   * // level might be 'h1', 'h2', or 'h3'
   */
  getActiveHeadingLevel() {
    return pageBuilderStore.bodyStylesState.activeHeadingLevel;
  }

  /**
   * Returns an inline style object for the font size of a given heading level (h1–h6).
   * If value not set, returns undefined.
   */
  getHeadingFontSizeStyle(headingLevel: number): React.CSSProperties | undefined {
    // Clamp headingLevel between 1–6, fallback to 1 if invalid
    const level = Math.min(Math.max(headingLevel || 1, 1), 6);
    
    const typography = pageBuilderStore.bodyStylesState.typography;
    if (!typography) return undefined; // Not initialized

    const roleKey = `h${level}` as keyof ITypoStyles;
    const role = typography[roleKey];
    if (role?.fontSize?.value && role?.fontSize?.unit) {
      return { fontSize: `${role.fontSize.value}${role.fontSize.unit}` };
    }
    return undefined;
  }

  /** Returns the entire body styles state object. */
  getBodyStylesState(): IBodyStyles {
    return pageBuilderStore.bodyStylesState;
  }

  /** Sets the full body styles state object. */
  setBodyStylesState(newState: IBodyStyles) {
    pageBuilderStore.bodyStylesState = newState;
  }

  /** Gets the current layout styles. */
  getLayoutStyles(): ILayoutStyles | undefined {
    return pageBuilderStore.bodyStylesState.layout ?? undefined;
  }

  /** Sets the layout styles. */
  setLayoutStyles(layout: ILayoutStyles) {
    pageBuilderStore.bodyStylesState.layout = layout;
  }

  /** Gets the current typography styles. */
  getTypographyStyles(): ITypoStyles | undefined {
    return pageBuilderStore.bodyStylesState.typography ?? undefined;
  }

  /** Sets the typography styles. */
  setTypographyStyles(typography: ITypoStyles) {
    pageBuilderStore.bodyStylesState.typography = typography;
  }

  /**
   * Gets the styles for a specific typography role (e.g., "h1", "body").
   * Returns undefined if typography state is not initialized.
   */
  getTypographyRole(role: keyof ITypoStyles): ITypographyRole | undefined {
    const typography = pageBuilderStore.bodyStylesState.typography;
    if (!typography) return undefined;
    return typography[role];
  }

  /**
   * Updates the styles for a specific typography role.
   * If typography state is not initialized, initializes it first.
   */
  setTypographyRole(role: keyof ITypoStyles, value: ITypographyRole) {
    if (!pageBuilderStore.bodyStylesState.typography) {
      pageBuilderStore.bodyStylesState.typography = {} as ITypoStyles;
    }
    pageBuilderStore.bodyStylesState.typography[role] = value;
  }

  /**
   * Converts a raw CSS selector from imported global CSS (e.g., ".title", "a", ".carousel-caption")
   * into the appropriate ITypoStyles key for internal state.
   *
   * - Maps known class selectors (e.g., ".title", ".caption", ".quote") to their core roles.
   * - Maps "a" and "p" tags to "links" and "p" roles, respectively.
   * - Strips leading dot from custom classes for use as custom role keys (e.g., ".my-custom" → "my-custom").
   * - Leaves tag selectors (e.g., "h1") and unknown selectors as-is.
   *
   * @param selector The raw CSS selector (e.g., ".title", "h1", "a", ".custom-block")
   * @returns The normalized ITypoStyles key (e.g., "title", "links", "carousel-caption", "h1")
   *
   * @example
   *   normalizeSelectorForTypoStyles('.title')         // "title"
   *   normalizeSelectorForTypoStyles('.my-caption')    // "my-caption"
   *   normalizeSelectorForTypoStyles('a')              // "links"
   *   normalizeSelectorForTypoStyles('h1')             // "h1"
   *   normalizeSelectorForTypoStyles('.carousel-hero') // "carousel-hero"
   */
  normalizeSelectorForTypoStyles(selector: string): string {
    // Map CSS class selectors to core keys if they match
    if (selector === ".title") return "title";
    if (selector === ".caption") return "caption";
    if (selector === ".quote") return "quote";
    // "links" is mapped from "a" below
    if (selector === "a") return "links";
    if (selector === "p") return "p";
    // Add more if needed

    // For anything else, remove leading dot for custom keys, e.g., ".my-custom" → "my-custom"
    if (selector.startsWith(".")) return selector.substring(1);

    // Otherwise, return as is (handles h1–h6, custom tags, etc.)
    return selector;
  }

  /**
   * Parses a CSS dimension string (e.g., "2rem", "16px", "100%") into an object
   * with `value` and `unit` properties for use in ITypographyRole or ILayoutStyles.
   *
   * @param str      The raw CSS dimension string to parse (e.g., "2rem", "16px", "100%").
   * @param fallback The unit to use if the string has no explicit unit (default: "rem").
   * @returns        An object with { value, unit } (e.g., { value: "2", unit: "rem" }).
   *                 If input is empty, returns { value: "", unit: fallback }.
   *
   * @example
   *   parseDimension("2rem")   // { value: "2", unit: "rem" }
   *   parseDimension("16px")   // { value: "16", unit: "px" }
   *   parseDimension("100%")   // { value: "100", unit: "%" }
   *   parseDimension("1.5")    // { value: "1.5", unit: "rem" }
   *   parseDimension("")       // { value: "", unit: "rem" }
   */
  parseDimension(str: string, fallback = "rem") {
    if (!str) return { value: "", unit: fallback };
    const match = str.match(/^([\d.]+)([a-z%]*)$/i);
    return match
      ? { value: match[1], unit: match[2] || fallback }
      : { value: str, unit: fallback };
  }

  /**
   * Maps a raw CSS property dictionary (e.g., from global-css.json or computed styles)
   * into a strongly-typed ITypographyRole object for use in the PageBuilder state.
   *
   * Supports standard typography properties (font size, weight, color, family, etc.),
   * as well as hover color, text decoration, and advanced features as needed.
   * Uses `parseDimension` to convert dimension strings to {value, unit} objects.
   *
   * @param styles   Dictionary of CSS property-value pairs (e.g., { "font-size": "2rem", ... }).
   * @returns        An ITypographyRole object with the relevant style properties set.
   *
   * @example
   *   mapCssToTypographyRole({
   *     "font-size": "2rem",
   *     "font-weight": "700",
   *     "color": "#333",
   *     "font-family": "Inter, sans-serif"
   *   })
   *   // =>
   *   // {
   *   //   fontSize: { value: "2", unit: "rem" },
   *   //   fontWeight: "700",
   *   //   color: "#333",
   *   //   fontFamily: "Inter, sans-serif"
   *   // }
   */
  mapCssToTypographyRole(styles: Record<string, string>): ITypographyRole {
    const role: ITypographyRole = {};
    if (styles["font-size"]) {
      role.fontSize = this.parseDimension(styles["font-size"]);
    }
    if (styles["font-weight"]) {
      role.fontWeight = styles["font-weight"];
    }
    if (styles["color"]) {
      role.color = styles["color"];
    }
    if (styles["font-style"]) {
      role.fontStyle = styles["font-style"];
    }
    if (styles["text-decoration"]) {
      role.textDecoration = styles["text-decoration"];
    }
    if (styles["text-transform"]) {
      role.textTransform = styles["text-transform"] as TextTransformType;
    }
    if (styles["line-height"]) {
      role.lineHeight = this.parseDimension(styles["line-height"]);
    }
    if (styles["letter-spacing"]) {
      role.letterSpacing = this.parseDimension(styles["letter-spacing"], "px");
    }
    if (styles["font-family"]) {
      role.fontFamily = styles["font-family"];
    }
    if (styles["hover-color"]) {
      role.hoverColor = styles["hover-color"];
    }
    // ...add more as your feature set grows
    return role;
  }

  /**
   * Serializes the main body container's layout and (optionally) typography settings
   * into a single CSS `body { ... }` block.
   *
   * - Includes background color, layout (boxed/full width), max-width, and spacing.
   * - Serializes padding and margin if set (including zero values).
   * - Optionally includes typography fields (font, color, line height, etc.) if provided.
   * - Outputs only properties that are set and valid; skips empty/undefined fields.
   *
   * @param layout     The layout settings object (see ILayoutStyles), may be undefined.
   * @param bodyRole   The body-level typography settings (see ITypographyRole), may be undefined.
   * @returns          The complete CSS string for the body block (including newline at end).
   *
   * @example
   *   bodyToCss(layout, bodyTypography)
   *   // =>
   *   // body {
   *   //   background-color: #fff;
   *   //   max-width: 1200px;
   *   //   margin-left: auto;
   *   //   margin-right: auto;
   *   //   width: 100%;
   *   //   padding: 32px;
   *   //   font-family: Inter, sans-serif;
   *   //   font-size: 1rem;
   *   //   ...
   *   // }
   */
  bodyToCss(
    layout: ILayoutStyles | undefined,
    bodyRole: ITypographyRole | undefined
  ): string {
    let css = "";
    if (!layout && !bodyRole) return css;
    css += "body {\n";
    // Layout properties
    if (layout?.backgroundColor) css += `  background-color: ${layout.backgroundColor};\n`;

    // Width logic
    if (layout?.containerType === "full") {
      css += `  width: 100%;\n`;
    } else if (layout?.containerType === "boxed" && layout?.maxWidth) {
      css += `  max-width: ${blocksManager.dimensionToCss(layout.maxWidth)};\n`;
      css += `  margin-left: auto;\n  margin-right: auto;\n  width: 100%;\n`;
    }

    // Padding/margin: only output if a value is truly set (including "0")
    const paddingCss = blocksManager.dimensionToCss(layout?.padding);
    if (typeof paddingCss === "string" && paddingCss !== "") css += `  padding: ${paddingCss};\n`;

    const marginCss = blocksManager.dimensionToCss(layout?.margin);
    if (typeof marginCss === "string" && marginCss !== "") css += `  margin: ${marginCss};\n`;

    // Typography for body
    if (bodyRole?.fontFamily) css += `  font-family: ${bodyRole.fontFamily};\n`;
    if (bodyRole?.fontSize) css += `  font-size: ${blocksManager.dimensionToCss(bodyRole.fontSize)};\n`;
    if (bodyRole?.fontWeight) css += `  font-weight: ${bodyRole.fontWeight};\n`;
    if (bodyRole?.color) css += `  color: ${bodyRole.color};\n`;
    if (bodyRole?.lineHeight) css += `  line-height: ${blocksManager.dimensionToCss(bodyRole.lineHeight)};\n`;
    if (bodyRole?.letterSpacing) css += `  letter-spacing: ${blocksManager.dimensionToCss(bodyRole.letterSpacing)};\n`;
    if (bodyRole?.fontStyle) css += `  font-style: ${bodyRole.fontStyle};\n`;
    if (bodyRole?.textDecoration) css += `  text-decoration: ${bodyRole.textDecoration};\n`;
    if (bodyRole?.textTransform) css += `  text-transform: ${bodyRole.textTransform};\n`;

    css += "}\n";
    return css;
  }

  /**
   * Serializes all non-body typography roles (core and custom) into CSS string blocks.
   *
   * - Maps internal role keys (e.g., "p", "caption", "links", "title") to their CSS selectors using selectorMap.
   * - Skips any roles listed in skipKeys (e.g., use ["body"] to avoid duplicate output).
   * - Handles both core keys (headings, paragraph, etc.) and custom selectors.
   * - For each role, outputs a block only if it has at least one style property.
   * - Outputs :hover style for any role with a hoverColor property.
   *
   * @param typo      ITypoStyles object containing all typography roles/selectors.
   * @param skipKeys  Array of role keys to skip (e.g., ["body"]).
   * @returns         The full CSS string for all roles except those skipped.
   *
   * @example
   *   typographyToCss(typography, ["body"]);
   *
   * @see selectorMap for role-to-selector logic.
   */
  typographyToCss(
    typo: ITypoStyles | undefined,
    skipKeys: string[] = []
  ): string {
    if (!typo) return "";
    let css = "";

    // Map internal role keys to CSS selectors
    const selectorMap: Record<string, string> = {
      p: "p",
      caption: ".caption",
      links: "a",
      title: ".title",
      // h1–h6 and quote output as themselves
    };

    const processed = new Set<string>();
    const coreKeys = [
      "title", "h1", "h2", "h3", "h4", "h5", "h6", "p", "caption", "quote", "links"
    ];

    // Output core roles, skipping any in skipKeys
    for (const key of coreKeys) {
      if (skipKeys.includes(key)) continue;
      const role = typo[key];
      if (!role) continue;
      processed.add(key);

      const selector = selectorMap[key] || key;
      let block = "";

      if (role.fontFamily) block += `  font-family: ${role.fontFamily};\n`;
      if (role.fontSize) block += `  font-size: ${blocksManager.dimensionToCss(role.fontSize)};\n`;
      if (role.fontWeight) block += `  font-weight: ${role.fontWeight};\n`;
      if (role.lineHeight) block += `  line-height: ${blocksManager.dimensionToCss(role.lineHeight)};\n`;
      if (role.letterSpacing) block += `  letter-spacing: ${blocksManager.dimensionToCss(role.letterSpacing)};\n`;
      if (role.color) block += `  color: ${role.color};\n`;
      if (role.fontStyle) block += `  font-style: ${role.fontStyle};\n`;
      if (role.textDecoration) block += `  text-decoration: ${role.textDecoration};\n`;
      if (role.textTransform) block += `  text-transform: ${role.textTransform};\n`;

      if (block) {
        css += `${selector} {\n${block}}\n`;
      }

      if (role.hoverColor) {
        css += `${selector}:hover {\n  color: ${role.hoverColor};\n}\n`;
      }
    }

    // Output custom selectors (not in coreKeys or skipKeys)
    for (const key of Object.keys(typo)) {
      if (processed.has(key) || skipKeys.includes(key)) continue;
      const role = typo[key];
      if (!role) continue;
      const selector = key; // Use custom selector as-is
      let block = "";

      if (role.fontFamily) block += `  font-family: ${role.fontFamily};\n`;
      if (role.fontSize) block += `  font-size: ${blocksManager.dimensionToCss(role.fontSize)};\n`;
      if (role.fontWeight) block += `  font-weight: ${role.fontWeight};\n`;
      if (role.lineHeight) block += `  line-height: ${blocksManager.dimensionToCss(role.lineHeight)};\n`;
      if (role.letterSpacing) block += `  letter-spacing: ${blocksManager.dimensionToCss(role.letterSpacing)};\n`;
      if (role.color) block += `  color: ${role.color};\n`;
      if (role.fontStyle) block += `  font-style: ${role.fontStyle};\n`;
      if (role.textDecoration) block += `  text-decoration: ${role.textDecoration};\n`;
      if (role.textTransform) block += `  text-transform: ${role.textTransform};\n`;

      if (block) {
        css += `${selector} {\n${block}}\n`;
      }

      if (role.hoverColor) {
        css += `${selector}:hover {\n  color: ${role.hoverColor};\n}\n`;
      }
    }

    return css;
  }
}

const bodyStylesManager = new BodyStylesManager();
export default bodyStylesManager;
